'use strict';

/**
 * SegmentIO Anaytics Class
 *
 * Adapted form the SegmentIO Node Library. The main difference is the use of
 * Angular `$http` instead of `superagent`. The rest of the code was essentially
 * copied as is so all the method signatures are the same.
 *
 * @see  https://github.com/segmentio/analytics-node
 */

var _ = require('lodash');
var assert = require('assert');
var uuid = require('node-uuid');

function SegmentIOFactory ($http, $q, $window) {

  /**
   * Initialize a new `Analytics` with your Segment project's `writeKey` and an
   * optional dictionary of `options`.
   *
   * @param {String} writeKey
   * @param {Object} options (optional)
   *   @property {Number} flushAt (default: 20)
   *   @property {Number} flushAfter (default: 10000)
   *   @property {String} host (default: 'https://api.segment.io')
   */

  function Analytics (writeKey, options) {

    if (!(this instanceof Analytics)) {
      return new Analytics(writeKey, options);
    }

    assert(writeKey, 'You must pass your Segment project\'s write key.');

    options = options || {};
    this.queue = [];
    this.writeKey = writeKey;
    this.host = options.host || 'https://api.segment.io';
    this.flushAt = Math.max(options.flushAt, 1) || 20;
    this.flushAfter = options.flushAfter || 10000;
  }

  /**
   * Send an identify `message`.
   *
   * @param {Object} message
   * @param {Function} fn (optional)
   * @return {Analytics}
   */

  Analytics.prototype.identify = function (message, fn) {
    assert(message.anonymousId || message.userId, 'You must pass either an "anonymousId" or a "userId".');
    this.enqueue('identify', message, fn);
    return this;
  };

  /**
   * Send a group `message`.
   *
   * @param {Object} message
   * @param {Function} fn (optional)
   * @return {Analytics}
   */

  Analytics.prototype.group = function (message, fn) {
    assert(message.anonymousId || message.userId, 'You must pass either an "anonymousId" or a "userId".');
    assert(message.groupId, 'You must pass a "groupId".');
    this.enqueue('group', message, fn);
    return this;
  };

  /**
   * Send a track `message`.
   *
   * @param {Object} message
   * @param {Function} fn (optional)
   * @return {Analytics}
   */

  Analytics.prototype.track = function (message, fn) {
    assert(message.anonymousId || message.userId, 'You must pass either an "anonymousId" or a "userId".');
    assert(message.event, 'You must pass an "event".');
    this.enqueue('track', message, fn);
    return this;
  };

  /**
   * Send a page `message`.
   *
   * @param {Object} message
   * @param {Function} fn (optional)
   * @return {Analytics}
   */

  Analytics.prototype.page = function (message, fn) {
    assert(message.anonymousId || message.userId, 'You must pass either an "anonymousId" or a "userId".');
    this.enqueue('page', message, fn);
    return this;
  };

  /**
   * Send an alias `message`.
   *
   * @param {Object} message
   * @param {Function} fn (optional)
   * @return {Analytics}
   */

  Analytics.prototype.alias = function (message, fn) {
    assert(message.userId, 'You must pass a "userId".');
    assert(message.previousId, 'You must pass a "previousId".');
    this.enqueue('alias', message, fn);
    return this;
  };

  /**
   * Flush the current queue and callback `fn (err, batch)`.
   *
   * @param {Function} fn (optional)
   * @return {Analytics}
   */

  Analytics.prototype.flush = function (fn) {
    fn = fn || _.noop;

    if (!this.queue.length) {
      fn(); // execute the callback immediately
      return $q.resolve();
    }

    var items = this.queue.splice(0, this.flushAt);
    var fns = items.map(function (_) {
      return _.callback;
    });
    var batch = items.map(function (_) { return _.message; });

    var data = {
      batch: batch,
      timestamp: new Date(),
      sentAt: new Date(),
      messageId: uuid.v4()
    };

    console.info('flush: %o', data);

    var req = {
      method: 'POST',
      url: this.host + '/v1/batch',
      headers: {
        Authorization: ['Basic', getAuthHeader(this.writeKey, '')].join(' ')
      },
      data: data
    }

    return $http(req).then (
    // success handler
    function (res) {
      fns.push(fn);
      fns.forEach(function (fn) {
        fn(null, data);
      });
      console.info('flushed: %o', data);
    },
    // error handler
    function (res) {
      var err = error(res);
      fns.push(fn);
      fns.forEach(function (fn) {
        fn(err, data);
      });
      console.info('flushed with error: %o', data);
    });
  };

  /**
   * Add a `message` of type `type` to the queue and check whether it should be
   * flushed.
   *
   * @param {String} type
   * @param {Object} message
   * @param {Functino} fn (optional)
   * @api private
   */

  Analytics.prototype.enqueue = function (type, message, fn) {
    fn = fn || _.noop;
    message = _.clone(message, true);
    message.type = type;

    if (!message.timestamp) {
      message.timestamp = new Date();
    }

    console.info('%s: %o', type, message);
    this.queue.push({
      message: message,
      callback: fn
    });

    if (this.queue.length >= this.flushAt) this.flush();
    if (this.timer) clearTimeout(this.timer);
    if (this.flushAfter) this.timer = setTimeout(this.flush.bind(this), this.flushAfter);
  };

  //////////////////////////////////////////////////////////////////////////////
  // Helpers
  //////////////////////////////////////////////////////////////////////////////

  function getAuthHeader (username, pass) {
    var string = [username, pass].join (':');

    // Base64 encode it
    return $window.btoa(string);
  }

  /**
   * Get an error from a `res`.
   *
   * @param {Object} res
   * @return {String}
   */

  function error(res) {
    if (!res.error) return;
    var body = res.body;
    var msg = body.error && body.error.message
      || res.status + ' ' + res.text;
    return new Error(msg);
  }

  return Analytics;
}


SegmentIOFactory.$inject = ['$http', '$q', '$window'];
module.exports = SegmentIOFactory;



// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/analytics/src/lib/SegmentIO.js